import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { appointmentService } from '../../../services/appointmentService';
import { realtimeService } from '../../../services/realtimeService';
import { useAuth } from '../../../contexts/AuthContext';

const UpcomingAppointments = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Only load appointments if user is authenticated
    if (!user) {
      setLoading(false);
      return;
    }

    loadAppointments();

    // Subscribe to real-time appointments changes
    const subscription = realtimeService?.subscribeToAppointments((payload) => {
      const { eventType, new: newRecord, old: oldRecord } = payload;

      if (eventType === 'INSERT') {
        // Add new appointment to the list
        const newAppointment = {
          id: newRecord?.id,
          helperName: 'Helper', // Will be populated from join
          helperRole: 'Professional',
          helperImage: '/assets/images/no_image.png',
          helperImageAlt: 'Helper profile picture',
          date: newRecord?.appointment_date,
          time: newRecord?.appointment_time,
          duration: `${newRecord?.duration} min`,
          sessionType: newRecord?.session_type,
          location: newRecord?.location || 'Online',
          topic: newRecord?.topic,
          preparationNotes: newRecord?.preparation_notes,
          status: newRecord?.status,
          minutesUntil: 120 // Default value
        };
        setAppointments((prev) => [newAppointment, ...prev]?.sort((a, b) => {
          const dateA = new Date(`${a.date}T${a.time}`);
          const dateB = new Date(`${b.date}T${b.time}`);
          return dateA - dateB;
        }));
      } else if (eventType === 'UPDATE') {
        // Update existing appointment
        setAppointments((prev) =>
          prev?.map((appointment) =>
            appointment?.id === newRecord?.id
              ? {
                  ...appointment,
                  date: newRecord?.appointment_date,
                  time: newRecord?.appointment_time,
                  duration: `${newRecord?.duration} min`,
                  sessionType: newRecord?.session_type,
                  location: newRecord?.location || 'Online',
                  topic: newRecord?.topic,
                  preparationNotes: newRecord?.preparation_notes,
                  status: newRecord?.status
                }
              : appointment
          )
        );
      } else if (eventType === 'DELETE') {
        // Remove deleted appointment
        setAppointments((prev) => prev?.filter((appointment) => appointment?.id !== oldRecord?.id));
      }
    });

    // Cleanup subscription on unmount
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const loadAppointments = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { data, error: fetchError } = await appointmentService?.getUserAppointments('upcoming');
      
      if (fetchError) {
        setError(fetchError?.message || 'Failed to load appointments');
        return;
      }
      
      // Also get scheduled appointments
      const { data: scheduledData } = await appointmentService?.getUserAppointments('scheduled');
      
      // Combine and sort by date/time
      const allAppointments = [...(data || []), ...(scheduledData || [])]?.sort((a, b) => {
          const dateA = new Date(`${a.date}T${a.time}`);
          const dateB = new Date(`${b.date}T${b.time}`);
          return dateA - dateB;
        });
      
      setAppointments(allAppointments);
    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!confirm('Are you sure you want to cancel this appointment?')) return;
    
    try {
      const { error: cancelError } = await appointmentService?.cancelAppointment(appointmentId);
      
      if (cancelError) {
        alert(`Failed to cancel: ${cancelError?.message}`);
        return;
      }
      
      // Reload appointments
      loadAppointments();
    } catch (err) {
      alert(`Failed to cancel: ${err?.message}`);
    }
  };

  const getStatusBadge = (status, minutesUntil) => {
    if (minutesUntil <= 60) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-warning/10 text-warning">
          <Icon name="Clock" size={16} />
          <span className="text-sm font-medium">Starting Soon</span>
        </div>);

    }
    return (
      <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary">
        <Icon name="Calendar" size={16} />
        <span className="text-sm font-medium">Scheduled</span>
      </div>);

  };

  const formatDateTime = (date, time) => {
    const dateObj = new Date(`${date}T${time}`);
    const options = { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    return dateObj?.toLocaleDateString('en-US', options);
  };

  const handleJoinSession = (appointmentId) => {
    navigate('/session-room', { state: { appointmentId } });
  };

  const handleReschedule = (appointmentId) => {
    navigate('/booking-scheduling', { state: { reschedule: true, appointmentId } });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-2 text-gray-600">Loading appointments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-8">
          <p className="text-error mb-4">{error}</p>
          <button 
            onClick={loadAppointments}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (appointments?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Upcoming Appointments</h2>
        </div>
        <div className="text-center py-8">
          <Icon name="Calendar" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">No upcoming appointments</p>
          <button
            onClick={() => navigate('/helper-discovery')}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Find a Helper
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-foreground">Upcoming Appointments</h2>
        <Button
          variant="outline"
          iconName="Plus"
          iconPosition="left"
          onClick={() => navigate('/booking-scheduling')}>

          Book New Session
        </Button>
      </div>
      <div className="space-y-4">
          {appointments?.map((appointment) =>
        <div key={appointment?.id} className="card-elevated p-6 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Image
                src={appointment?.helperImage}
                alt={appointment?.helperImageAlt}
                className="w-20 h-20 rounded-lg object-cover" />

                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">{appointment?.helperName}</h3>
                      <p className="text-sm text-muted-foreground">{appointment?.helperRole}</p>
                    </div>
                    {getStatusBadge(appointment?.status, appointment?.minutesUntil)}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Calendar" size={16} className="text-primary" />
                      <span className="text-foreground">{formatDateTime(appointment?.date, appointment?.time)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Clock" size={16} className="text-primary" />
                      <span className="text-foreground">{appointment?.duration}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Video" size={16} className="text-primary" />
                      <span className="text-foreground">{appointment?.sessionType}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="MapPin" size={16} className="text-primary" />
                      <span className="text-foreground">{appointment?.location}</span>
                    </div>
                  </div>

                  <div className="bg-muted rounded-lg p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <Icon name="FileText" size={16} className="text-primary mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground mb-1">Topic: {appointment?.topic}</p>
                        <p className="text-sm text-muted-foreground">{appointment?.preparationNotes}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {appointment?.minutesUntil <= 60 ?
                <Button
                  variant="default"
                  iconName="Video"
                  iconPosition="left"
                  onClick={() => handleJoinSession(appointment?.id)}>

                        Join Session
                      </Button> :

                <Button
                  variant="outline"
                  iconName="FileText"
                  iconPosition="left">

                        View Preparation
                      </Button>
                }
                    <Button
                  variant="ghost"
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={() => handleReschedule(appointment?.id)}>

                      Reschedule
                    </Button>
                    <button
                      onClick={() => handleCancelAppointment(appointment?.id)}
                      className="text-error hover:text-error/80 text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}
        </div>
    </div>);

};

export default UpcomingAppointments;